import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import {
  Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle,
  Wind, Droplets, Loader2, Thermometer, Umbrella, SunDim, ChevronDown, ChevronUp
} from "lucide-react";

const GOES_COORDS = { lat: 51.5, lon: 3.89 };

// --- Types ---
interface HourlyForecast {
  hour: string;
  temp: number;
  icon: string;
  precip: number;
  windDir: number;
  rawHour: number;
}

interface DailyForecast {
  dayName: string;
  icon: string;
  tempHigh: number;
  tempLow: number;
  precip: number;
  uvIndex: number;
}

interface WeatherData {
  temp: number;
  feelsLike: number;
  description: string;
  icon: string;
  humidity: number;
  windSpeed: number;
  city: string;
  tempHigh: number;
  tempLow: number;
  uvIndex: number;
  precipChance: number;
  sunrise: string;
  sunset: string;
  hourly: HourlyForecast[];
  daily: DailyForecast[];
}

// --- Icon map ---
const weatherIcons: Record<string, React.ElementType> = {
  "01": Sun, "02": Cloud, "03": Cloud, "04": Cloud,
  "09": CloudDrizzle, "10": CloudRain, "11": CloudLightning,
  "13": CloudSnow, "50": Wind,
};

const wmoToInfo: Record<number, { desc: string; icon: string }> = {
  0: { desc: "Helder", icon: "01" },
  1: { desc: "Overwegend helder", icon: "02" },
  2: { desc: "Half bewolkt", icon: "03" },
  3: { desc: "Bewolkt", icon: "04" },
  45: { desc: "Mistig", icon: "50" },
  48: { desc: "Rijpmist", icon: "50" },
  51: { desc: "Lichte motregen", icon: "09" },
  53: { desc: "Motregen", icon: "09" },
  55: { desc: "Zware motregen", icon: "09" },
  61: { desc: "Lichte regen", icon: "10" },
  63: { desc: "Regen", icon: "10" },
  65: { desc: "Zware regen", icon: "10" },
  71: { desc: "Lichte sneeuw", icon: "13" },
  73: { desc: "Sneeuw", icon: "13" },
  75: { desc: "Zware sneeuw", icon: "13" },
  80: { desc: "Regenbuien", icon: "10" },
  81: { desc: "Zware buien", icon: "10" },
  82: { desc: "Hevige buien", icon: "10" },
  95: { desc: "Onweer", icon: "11" },
  96: { desc: "Onweer met hagel", icon: "11" },
  99: { desc: "Zwaar onweer", icon: "11" },
};

const dayNames = ["Zo", "Ma", "Di", "Wo", "Do", "Vr", "Za"];

// --- Gradient ---
function getWeatherGradient(icon: string, hour: number): string {
  const isNight = hour < 6 || hour >= 21;
  const isDusk = (hour >= 18 && hour < 21) || (hour >= 6 && hour < 8);
  if (isNight) return "linear-gradient(180deg, #1a1b3a 0%, #2d1b69 40%, #0f0c29 100%)";
  if (isDusk) return "linear-gradient(180deg, #2b5876 0%, #4e4376 50%, #1b1464 100%)";
  switch (icon) {
    case "01": return "linear-gradient(180deg, #4a90d9 0%, #67b8f0 40%, #a8d8f0 100%)";
    case "02": return "linear-gradient(180deg, #5a9bd5 0%, #7fb3d8 50%, #b0cfe0 100%)";
    case "03": case "04": return "linear-gradient(180deg, #6b7b8d 0%, #8899a6 50%, #a5b5c0 100%)";
    case "09": case "10": return "linear-gradient(180deg, #4a5568 0%, #5f7083 50%, #718096 100%)";
    case "11": return "linear-gradient(180deg, #2d3748 0%, #4a5568 50%, #5a6577 100%)";
    case "13": return "linear-gradient(180deg, #94a3b8 0%, #b0bec5 50%, #cfd8dc 100%)";
    case "50": return "linear-gradient(180deg, #78909c 0%, #90a4ae 50%, #b0bec5 100%)";
    default: return "linear-gradient(180deg, #4a90d9 0%, #67b8f0 40%, #a8d8f0 100%)";
  }
}

// --- Animated backgrounds ---
const animStyles = `
@keyframes wdrift { 0%{transform:translateX(-20%)} 100%{transform:translateX(120%)} }
@keyframes wdrift-slow { 0%{transform:translateX(-30%)} 100%{transform:translateX(130%)} }
@keyframes wdrift-mid { 0%{transform:translateX(-25%)} 100%{transform:translateX(125%)} }
@keyframes wfloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
@keyframes wfall { 0%{transform:translateY(-10%) translateX(0) rotate(15deg)} 100%{transform:translateY(110%) translateX(8px) rotate(15deg)} }
@keyframes wsnow { 0%{transform:translateY(-10%) translateX(0)} 50%{transform:translateY(50%) translateX(20px)} 100%{transform:translateY(110%) translateX(-5px)} }
@keyframes wpulse { 0%,100%{opacity:0.15;transform:scale(1)} 50%{opacity:0.35;transform:scale(1.15)} }
@keyframes wflash { 0%,90%,100%{opacity:0} 92%{opacity:0.6} 94%{opacity:0} 96%{opacity:0.4} }
@keyframes wrotate { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
@keyframes wsplash { 0%{transform:scaleX(0) scaleY(0);opacity:0.6} 100%{transform:scaleX(1.5) scaleY(0.3);opacity:0} }
@keyframes wtwinkle { 0%,100%{opacity:0.1;transform:scale(0.8)} 50%{opacity:0.8;transform:scale(1.2)} }
@keyframes wfog { 0%{transform:translateX(-30%);opacity:0.1} 50%{opacity:0.25} 100%{transform:translateX(30%);opacity:0.1} }
@keyframes wglow { 0%,100%{opacity:0;transform:scale(0.8)} 50%{opacity:0.4;transform:scale(1.2)} }
@keyframes wshimmer { 0%{opacity:0.03} 50%{opacity:0.08} 100%{opacity:0.03} }
`;

const WeatherAnimations = React.memo(({ icon, hour }: { icon: string; hour: number }) => {
  const isNight = hour < 6 || hour >= 21;
  const elements = useMemo(() => {
    const layers: React.ReactNode[] = [];

    // Night overlay: twinkling stars
    if (isNight) {
      layers.push(
        <div key="stars" className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="absolute rounded-full bg-white"
              style={{
                width: `${1 + Math.random() * 1.5}px`,
                height: `${1 + Math.random() * 1.5}px`,
                top: `${5 + Math.random() * 60}%`,
                left: `${5 + Math.random() * 90}%`,
                animation: `wtwinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 3}s`,
                willChange: "transform, opacity",
              }} />
          ))}
          {/* Moon */}
          <div className="absolute top-3 right-6 w-8 h-8 rounded-full"
            style={{
              background: "radial-gradient(circle at 35% 35%, transparent 40%, rgba(255,255,200,0.15) 40%)",
              boxShadow: "0 0 15px 5px rgba(255,255,200,0.08)",
            }} />
        </div>
      );
    }

    switch (icon) {
      case "01": // sun - rays + glow rings
        layers.push(
          <div key="sun" className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Glow rings */}
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-yellow-300/20"
              style={{ animation: "wpulse 4s ease-in-out infinite", willChange: "transform, opacity" }} />
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-yellow-200/15"
              style={{ animation: "wpulse 3s ease-in-out infinite 1s", willChange: "transform, opacity" }} />
            <div className="absolute -top-2 -right-2 w-16 h-16 rounded-full bg-orange-200/10"
              style={{ animation: "wpulse 5s ease-in-out infinite 0.5s", willChange: "transform, opacity" }} />
            {/* Rotating rays */}
            <div className="absolute -top-4 -right-4 w-28 h-28"
              style={{ animation: "wrotate 30s linear infinite", willChange: "transform" }}>
              {[0, 45, 90, 135].map(deg => (
                <div key={deg} className="absolute top-1/2 left-1/2 w-px h-14 bg-yellow-200/10 origin-bottom"
                  style={{ transform: `translate(-50%, -100%) rotate(${deg}deg)` }} />
              ))}
            </div>
            {/* Heat shimmer */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-yellow-200/5 to-transparent"
              style={{ animation: "wshimmer 3s ease-in-out infinite" }} />
          </div>
        );
        break;

      case "02": case "03": case "04": { // clouds - iPhone style
        // CloudShape: overlapping circles for fluffy cumulus look
        const CloudShape = ({ size, opacity, blur, top, drift, duration, delay }: {
          size: number; opacity: number; blur: number; top: string; drift: string; duration: number; delay: number;
        }) => {
          const w = size;
          const h = size * 0.45;
          return (
            <div className="absolute" style={{
              top,
              filter: `blur(${blur}px)`,
              opacity,
              animation: `${drift} ${duration}s linear infinite, wfloat ${duration * 0.8}s ease-in-out infinite`,
              animationDelay: `${delay}s, ${delay * 0.5}s`,
              willChange: "transform, opacity",
            }}>
              {/* Main body */}
              <div className="absolute rounded-full bg-white" style={{
                width: `${w}px`, height: `${h}px`,
                top: `${h * 0.3}px`, left: `${w * 0.1}px`,
              }} />
              {/* Top bumps */}
              <div className="absolute rounded-full bg-white" style={{
                width: `${w * 0.45}px`, height: `${w * 0.45}px`,
                top: `0px`, left: `${w * 0.15}px`,
              }} />
              <div className="absolute rounded-full bg-white" style={{
                width: `${w * 0.55}px`, height: `${w * 0.55}px`,
                top: `${-h * 0.15}px`, left: `${w * 0.35}px`,
              }} />
              <div className="absolute rounded-full bg-white" style={{
                width: `${w * 0.4}px`, height: `${w * 0.4}px`,
                top: `${h * 0.05}px`, left: `${w * 0.6}px`,
              }} />
              {/* Small accent bump */}
              <div className="absolute rounded-full bg-white" style={{
                width: `${w * 0.3}px`, height: `${w * 0.3}px`,
                top: `${h * 0.15}px`, left: `${w * 0.82}px`,
              }} />
            </div>
          );
        };

        layers.push(
          <div key="clouds" className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Background layer - large, blurry, slow */}
            <CloudShape size={120} opacity={0.06} blur={6} top="5%" drift="wdrift-slow" duration={40} delay={0} />
            <CloudShape size={100} opacity={0.05} blur={8} top="15%" drift="wdrift-slow" duration={35} delay={12} />
            {/* Middle layer */}
            <CloudShape size={80} opacity={0.09} blur={3} top="25%" drift="wdrift-mid" duration={28} delay={5} />
            <CloudShape size={70} opacity={0.08} blur={4} top="45%" drift="wdrift-mid" duration={32} delay={18} />
            {/* Foreground layer - smaller, sharper, slightly faster */}
            <CloudShape size={55} opacity={0.12} blur={1} top="35%" drift="wdrift-mid" duration={22} delay={8} />
            <CloudShape size={45} opacity={0.1} blur={1.5} top="60%" drift="wdrift-mid" duration={25} delay={15} />
          </div>
        );
        break;
      }

      case "09": case "10": // rain
        layers.push(
          <div key="rain" className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Dark clouds */}
            {[0, 1, 2].map(i => (
              <div key={`rc${i}`} className="absolute rounded-full"
                style={{
                  width: `${60 + i * 25}px`, height: `${18 + i * 6}px`,
                  top: `${-2 + i * 5}%`,
                  background: `rgba(255,255,255,${0.04 + i * 0.02})`,
                  animation: `wdrift ${10 + i * 3}s linear infinite`,
                  animationDelay: `${i * 2}s`,
                  willChange: "transform",
                }} />
            ))}
            {/* Raindrops */}
            {Array.from({ length: 22 }).map((_, i) => (
              <div key={i} className="absolute bg-white/20 rounded-full"
                style={{
                  width: `${0.5 + Math.random() * 1}px`,
                  height: `${8 + Math.random() * 14}px`,
                  left: `${3 + (i / 22) * 94}%`,
                  animation: `wfall ${0.5 + Math.random() * 0.4}s linear infinite`,
                  animationDelay: `${Math.random() * 1}s`,
                  willChange: "transform",
                }} />
            ))}
            {/* Splashes */}
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`sp${i}`} className="absolute bottom-1 rounded-full bg-white/10"
                style={{
                  width: "4px", height: "2px",
                  left: `${10 + i * 15}%`,
                  animation: `wsplash ${0.8 + Math.random() * 0.4}s ease-out infinite`,
                  animationDelay: `${Math.random() * 1}s`,
                  willChange: "transform, opacity",
                }} />
            ))}
          </div>
        );
        break;

      case "13": // snow
        layers.push(
          <div key="snow" className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="absolute rounded-full bg-white"
                style={{
                  width: `${1 + Math.random() * 3}px`,
                  height: `${1 + Math.random() * 3}px`,
                  left: `${3 + (i / 18) * 94}%`,
                  opacity: 0.1 + Math.random() * 0.2,
                  animation: `wsnow ${3 + Math.random() * 3}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 3}s`,
                  willChange: "transform",
                }} />
            ))}
          </div>
        );
        break;

      case "11": // thunderstorm
        layers.push(
          <div key="storm" className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Cloud glow */}
            {[0, 1].map(i => (
              <div key={`cg${i}`} className="absolute rounded-full"
                style={{
                  width: `${50 + i * 30}px`, height: `${20 + i * 10}px`,
                  top: `${5 + i * 10}%`, left: `${20 + i * 25}%`,
                  background: "rgba(180,180,255,0.05)",
                  animation: `wglow ${3 + i * 2}s ease-in-out infinite`,
                  animationDelay: `${i * 1.5}s`,
                  willChange: "transform, opacity",
                }} />
            ))}
            {/* Lightning flashes */}
            <div className="absolute inset-0 bg-white/0"
              style={{ animation: "wflash 3s ease-in-out infinite" }} />
            <div className="absolute inset-0 bg-purple-200/0"
              style={{ animation: "wflash 5s ease-in-out infinite 1.5s" }} />
            {/* Rain overlay */}
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={`sr${i}`} className="absolute bg-white/15 rounded-full"
                style={{
                  width: "0.5px",
                  height: `${8 + Math.random() * 10}px`,
                  left: `${5 + (i / 12) * 90}%`,
                  animation: `wfall ${0.4 + Math.random() * 0.3}s linear infinite`,
                  animationDelay: `${Math.random() * 0.8}s`,
                  willChange: "transform",
                }} />
            ))}
          </div>
        );
        break;

      case "50": // mist/fog
        layers.push(
          <div key="fog" className="absolute inset-0 pointer-events-none overflow-hidden">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="absolute rounded-full"
                style={{
                  width: "120%", height: `${15 + i * 5}px`,
                  top: `${15 + i * 22}%`, left: "-10%",
                  background: `rgba(255,255,255,${0.06 + i * 0.03})`,
                  animation: `wfog ${8 + i * 3}s ease-in-out infinite`,
                  animationDelay: `${i * 2}s`,
                  willChange: "transform, opacity",
                }} />
            ))}
          </div>
        );
        break;
    }

    return layers.length > 0 ? <>{layers}</> : null;
  }, [icon, isNight]);
  return elements;
});
WeatherAnimations.displayName = "WeatherAnimations";

// --- Sun arc ---
const SunArc = ({ sunrise, sunset }: { sunrise: string; sunset: string }) => {
  const now = new Date();
  const rise = new Date(sunrise);
  const set = new Date(sunset);
  const totalDay = set.getTime() - rise.getTime();
  const elapsed = now.getTime() - rise.getTime();
  const progress = Math.max(0, Math.min(1, elapsed / totalDay));
  const isDay = progress > 0 && progress < 1;

  const riseStr = `${rise.getHours().toString().padStart(2, "0")}:${rise.getMinutes().toString().padStart(2, "0")}`;
  const setStr = `${set.getHours().toString().padStart(2, "0")}:${set.getMinutes().toString().padStart(2, "0")}`;

  // SVG arc
  const cx = 50, cy = 40, r = 35;
  const startAngle = Math.PI;
  const endAngle = 0;
  const sunAngle = startAngle + (endAngle - startAngle) * progress;
  const sunX = cx + r * Math.cos(sunAngle);
  const sunY = cy - r * Math.sin(sunAngle);

  return (
    <div className="mx-3 border-t border-white/20 pt-2 pb-2">
      <svg viewBox="0 0 100 50" className="w-full h-10">
        {/* Arc path */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="2,2"
        />
        {/* Filled arc */}
        {isDay && (
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${sunX} ${sunY}`}
            fill="none" stroke="rgba(255,200,50,0.5)" strokeWidth="1.5"
          />
        )}
        {/* Sun dot */}
        {isDay && (
          <circle cx={sunX} cy={sunY} r="3" fill="#fbbf24" opacity="0.9">
            <animate attributeName="r" values="3;3.5;3" dur="2s" repeatCount="indefinite" />
          </circle>
        )}
        {/* Horizon line */}
        <line x1={cx - r - 3} y1={cy} x2={cx + r + 3} y2={cy} stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
      </svg>
      <div className="flex justify-between px-1 -mt-1">
        <span className="text-[10px] text-white/50">↑ {riseStr}</span>
        <span className="text-[10px] text-white/50">↓ {setStr}</span>
      </div>
    </div>
  );
};

// --- Wind direction helper ---
function windDirLabel(deg: number): string {
  const dirs = ["N", "NO", "O", "ZO", "Z", "ZW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

// --- Main component ---
const DashboardWeather = () => {
  const [tab, setTab] = useState<"hourly" | "daily">("hourly");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  const { data: weather, isLoading } = useQuery<WeatherData>({
    queryKey: ["dashboard-weather-v2"],
    queryFn: async () => {
      const res = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${GOES_COORDS.lat}&longitude=${GOES_COORDS.lon}` +
        `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m` +
        `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,uv_index_max,sunrise,sunset,weather_code` +
        `&hourly=temperature_2m,weather_code,precipitation_probability,wind_direction_10m` +
        `&timezone=Europe%2FAmsterdam&forecast_days=3`
      );
      const data = await res.json();
      const current = data.current;
      const weatherInfo = wmoToInfo[current.weather_code] || { desc: "Onbekend", icon: "03" };

      const now = new Date();
      const currentHour = now.getHours();
      const hourly: HourlyForecast[] = [];
      for (let i = 0; i < 12; i++) {
        const idx = currentHour + i;
        if (idx < (data.hourly?.time?.length || 0)) {
          const hInfo = wmoToInfo[data.hourly.weather_code[idx]] || { desc: "", icon: "03" };
          const time = new Date(data.hourly.time[idx]);
          hourly.push({
            hour: i === 0 ? "Nu" : `${time.getHours()}:00`,
            temp: Math.round(data.hourly.temperature_2m[idx]),
            icon: hInfo.icon,
            precip: data.hourly.precipitation_probability?.[idx] ?? 0,
            windDir: data.hourly.wind_direction_10m?.[idx] ?? 0,
            rawHour: time.getHours(),
          });
        }
      }

      const daily: DailyForecast[] = [];
      for (let i = 0; i < (data.daily?.time?.length || 0); i++) {
        const d = new Date(data.daily.time[i]);
        const dInfo = wmoToInfo[data.daily.weather_code?.[i]] || { desc: "", icon: "03" };
        daily.push({
          dayName: i === 0 ? "Vandaag" : dayNames[d.getDay()],
          icon: dInfo.icon,
          tempHigh: Math.round(data.daily.temperature_2m_max[i]),
          tempLow: Math.round(data.daily.temperature_2m_min[i]),
          precip: data.daily.precipitation_probability_max?.[i] ?? 0,
          uvIndex: Math.round(data.daily.uv_index_max?.[i] ?? 0),
        });
      }

      return {
        temp: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.apparent_temperature),
        description: weatherInfo.desc,
        icon: weatherInfo.icon,
        humidity: current.relative_humidity_2m,
        windSpeed: Math.round(current.wind_speed_10m),
        city: "Goes",
        tempHigh: Math.round(data.daily.temperature_2m_max[0]),
        tempLow: Math.round(data.daily.temperature_2m_min[0]),
        uvIndex: Math.round(data.daily.uv_index_max?.[0] ?? 0),
        precipChance: data.daily.precipitation_probability_max?.[0] ?? 0,
        sunrise: data.daily.sunrise?.[0] ?? "",
        sunset: data.daily.sunset?.[0] ?? "",
        hourly,
        daily,
      };
    },
    refetchInterval: 600000,
    staleTime: 300000,
  });

  const currentHour = new Date().getHours();
  const gradient = weather ? getWeatherGradient(weather.icon, currentHour) : getWeatherGradient("03", 12);

  if (isLoading) {
    return (
      <Card className="overflow-hidden rounded-2xl border-0 shadow-lg min-w-[280px]" style={{ background: gradient }}>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-white/70" />
        </CardContent>
      </Card>
    );
  }

  if (!weather) return null;

  return (
    <>
      <style>{animStyles}</style>
      <Card className="overflow-hidden rounded-2xl border-0 shadow-lg relative min-w-[280px]" style={{ background: gradient }}>
        <WeatherAnimations icon={weather.icon} hour={currentHour} />
        <CardContent className="p-0 relative z-10">
          {/* Main info */}
          <div className="flex flex-col items-center pt-5 pb-2">
            <p className="text-sm font-medium tracking-wide text-white/90">{weather.city}</p>
            <p className="text-6xl font-extralight text-white leading-none mt-1">{weather.temp}°</p>
            <p className="text-sm text-white/80 mt-1 capitalize">{weather.description}</p>
            <p className="text-sm text-white/60 mt-0.5">
              H:{weather.tempHigh}° L:{weather.tempLow}°
            </p>
          </div>

          {/* Tab toggle */}
          <div className="flex mx-3 mt-1 mb-0 bg-white/10 rounded-lg p-0.5">
            <button
              onClick={() => { setTab("hourly"); setSelectedIdx(null); }}
              className={`flex-1 text-[11px] font-medium py-1 rounded-md transition-all ${tab === "hourly" ? "bg-white/20 text-white" : "text-white/50"}`}
            >
              Uurlijks
            </button>
            <button
              onClick={() => { setTab("daily"); setSelectedIdx(null); }}
              className={`flex-1 text-[11px] font-medium py-1 rounded-md transition-all ${tab === "daily" ? "bg-white/20 text-white" : "text-white/50"}`}
            >
              3 Dagen
            </button>
          </div>

          {/* Hourly forecast */}
          {tab === "hourly" && (
            <div className="mx-3 mt-2 border-t border-white/20 pt-3 pb-1">
              <div className="flex gap-0 overflow-x-auto no-scrollbar">
                {weather.hourly.map((h, i) => {
                  const Icon = weatherIcons[h.icon] || Cloud;
                  const isSelected = selectedIdx === i;
                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedIdx(isSelected ? null : i)}
                      className={`flex flex-col items-center flex-1 min-w-[44px] py-1 rounded-lg transition-all ${isSelected ? "bg-white/15" : "hover:bg-white/5"}`}
                    >
                      <span className="text-[11px] font-medium text-white/70">{h.hour}</span>
                      <Icon className="h-4 w-4 text-white/90 my-1" />
                      <span className="text-xs font-medium text-white">{h.temp}°</span>
                      {h.precip > 0 && (
                        <span className="text-[9px] text-blue-200/80">{h.precip}%</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Detail panel */}
              {selectedIdx !== null && weather.hourly[selectedIdx] && (
                <div className="mt-2 p-2 bg-white/10 rounded-lg text-white/80 text-[11px] flex gap-4 justify-center animate-fade-in">
                  <span className="flex items-center gap-1"><Umbrella className="h-3 w-3" /> {weather.hourly[selectedIdx].precip}%</span>
                  <span className="flex items-center gap-1"><Wind className="h-3 w-3" /> {windDirLabel(weather.hourly[selectedIdx].windDir)}</span>
                </div>
              )}
            </div>
          )}

          {/* Daily forecast */}
          {tab === "daily" && (
            <div className="mx-3 mt-2 border-t border-white/20 pt-3 pb-1">
              {weather.daily.map((d, i) => {
                const Icon = weatherIcons[d.icon] || Cloud;
                const isSelected = selectedIdx === i;
                return (
                  <div key={i}>
                    <button
                      onClick={() => setSelectedIdx(isSelected ? null : i)}
                      className={`w-full flex items-center py-1.5 px-1 rounded-lg transition-all ${isSelected ? "bg-white/15" : "hover:bg-white/5"}`}
                    >
                      <span className="text-xs text-white/80 w-14 text-left">{d.dayName}</span>
                      <Icon className="h-4 w-4 text-white/90 mx-2" />
                      {d.precip > 0 && <span className="text-[10px] text-blue-200/70 w-8">{d.precip}%</span>}
                      {d.precip === 0 && <span className="w-8" />}
                      <div className="flex-1 flex justify-end gap-2">
                        <span className="text-xs text-white/50">{d.tempLow}°</span>
                        {/* Temp bar */}
                        <div className="w-16 h-1 rounded-full bg-white/10 self-center relative overflow-hidden">
                          <div
                            className="absolute h-full rounded-full bg-gradient-to-r from-blue-300/60 to-orange-300/60"
                            style={{
                              left: `${((d.tempLow + 10) / 50) * 100}%`,
                              right: `${100 - ((d.tempHigh + 10) / 50) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-white">{d.tempHigh}°</span>
                      </div>
                      {isSelected ? <ChevronUp className="h-3 w-3 text-white/40 ml-1" /> : <ChevronDown className="h-3 w-3 text-white/40 ml-1" />}
                    </button>
                    {isSelected && (
                      <div className="px-2 pb-2 pt-1 flex gap-4 justify-center text-[11px] text-white/70 animate-fade-in">
                        <span className="flex items-center gap-1"><Umbrella className="h-3 w-3" /> {d.precip}%</span>
                        <span className="flex items-center gap-1"><SunDim className="h-3 w-3" /> UV {d.uvIndex}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Sun arc */}
          {weather.sunrise && weather.sunset && (
            <SunArc sunrise={weather.sunrise} sunset={weather.sunset} />
          )}

          {/* Details row */}
          <div className="flex mx-3 border-t border-white/20 py-3">
            <div className="flex-1 flex items-center justify-center gap-1.5">
              <Wind className="h-3.5 w-3.5 text-white/60" />
              <span className="text-xs text-white/80">{weather.windSpeed} km/u</span>
            </div>
            <div className="w-px bg-white/20" />
            <div className="flex-1 flex items-center justify-center gap-1.5">
              <Droplets className="h-3.5 w-3.5 text-white/60" />
              <span className="text-xs text-white/80">{weather.humidity}%</span>
            </div>
            <div className="w-px bg-white/20" />
            <div className="flex-1 flex items-center justify-center gap-1.5">
              <Umbrella className="h-3.5 w-3.5 text-white/60" />
              <span className="text-xs text-white/80">{weather.precipChance}%</span>
            </div>
            <div className="w-px bg-white/20" />
            <div className="flex-1 flex items-center justify-center gap-1.5">
              <SunDim className="h-3.5 w-3.5 text-white/60" />
              <span className="text-xs text-white/80">UV {weather.uvIndex}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default DashboardWeather;
